/**
 * Formats an ISO 8601 datetime string as a human-readable relative time.
 * Uses Intl.RelativeTimeFormat — no extra dependencies.
 *
 * Special case: values less than 60 seconds old return "live" for use in
 * last-updated chips on service health cards.
 */

const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

export function formatRelative(value: string): string {
  const diffMs = new Date(value).getTime() - Date.now();
  const absDiffSec = Math.abs(diffMs) / 1000;

  if (absDiffSec < 60) {
    return rtf.format(Math.round(diffMs / 1000), 'second');
  }
  if (absDiffSec < 3600) {
    return rtf.format(Math.round(diffMs / 60000), 'minute');
  }
  if (absDiffSec < 86400) {
    return rtf.format(Math.round(diffMs / 3600000), 'hour');
  }
  if (absDiffSec < 86400 * 30) {
    return rtf.format(Math.round(diffMs / 86400000), 'day');
  }
  if (absDiffSec < 86400 * 365) {
    return rtf.format(Math.round(diffMs / (86400000 * 30)), 'month');
  }
  return rtf.format(Math.round(diffMs / (86400000 * 365)), 'year');
}

/**
 * Returns "live" when less than 60 seconds ago, otherwise formatRelative.
 * Used by ServiceHealthCard last-updated chip.
 */
export function formatRelativeLive(value: string): string {
  const absDiffSec = Math.abs(new Date(value).getTime() - Date.now()) / 1000;
  if (absDiffSec < 60) {
    return 'live';
  }
  return formatRelative(value);
}
